import moment from 'moment-timezone';
import { PORT_STATUS, PORTING_TIME_ZONE, TnPortOrderError } from './tn-porting';
import { GroupSummaryStatus, SUBMISSION_ISSUE_STATUSES } from './tn-porting-group-status';

// Porting dashboard ("Manage Porting Orders"). The plan, with every tile definition
// agreed with the product owner, is thoughts/shared/plans/2026-10-19-porting-dashboard.md.
// The tile predicates themselves are SQL (cpaas-porting-dashboard-sql.ts) because the
// list paginates and the counts aggregate over every group; what lives here is what
// both sides of the wire need: the vocabulary, the date windows and the next-action text.

export const PORTING_DASHBOARD_TILE = {
  ACTIVE: 'ACTIVE',
  ACTION_REQUIRED: 'ACTION_REQUIRED',
  SUBMISSION_ISSUE: 'SUBMISSION_ISSUE',
  NEW_MESSAGES: 'NEW_MESSAGES',
  NOT_CONFIGURED: 'NOT_CONFIGURED',
  UPCOMING: 'UPCOMING',
  TF_FOC_PENDING: 'TF_FOC_PENDING',
  STALE: 'STALE',
  COMPLETED: 'COMPLETED',
} as const;

export type PortingDashboardTile = typeof PORTING_DASHBOARD_TILE[keyof typeof PORTING_DASHBOARD_TILE];

/** Tiles only Nuso admins see; the API refuses them to anyone else. */
export const PORTING_DASHBOARD_ADMIN_TILES: readonly PortingDashboardTile[] = [
  PORTING_DASHBOARD_TILE.SUBMISSION_ISSUE,
  PORTING_DASHBOARD_TILE.TF_FOC_PENDING,
  PORTING_DASHBOARD_TILE.STALE,
];

export const PORTING_DASHBOARD_TILE_LABELS: Record<PortingDashboardTile, string> = {
  [PORTING_DASHBOARD_TILE.ACTIVE]: 'Active Orders',
  [PORTING_DASHBOARD_TILE.ACTION_REQUIRED]: 'Action Required',
  [PORTING_DASHBOARD_TILE.SUBMISSION_ISSUE]: 'Submission Issues',
  [PORTING_DASHBOARD_TILE.NEW_MESSAGES]: 'New Messages',
  [PORTING_DASHBOARD_TILE.NOT_CONFIGURED]: 'Not Configured',
  [PORTING_DASHBOARD_TILE.UPCOMING]: 'Upcoming Ports',
  [PORTING_DASHBOARD_TILE.TF_FOC_PENDING]: 'TF Orders FOC Pending',
  [PORTING_DASHBOARD_TILE.STALE]: 'Stale Orders',
  [PORTING_DASHBOARD_TILE.COMPLETED]: 'Completed (30d)',
};

/** Days without a note, status change or audit entry before an order is stale. */
export const PORTING_DASHBOARD_STALE_DAYS = 7;
/** The Completed tile counts this many days; its list shows every completed order. */
export const PORTING_DASHBOARD_COMPLETED_COUNT_DAYS = 30;
/** Upcoming Ports' default range, and Not Configured's fixed one, in calendar days from today. */
export const PORTING_DASHBOARD_UPCOMING_DAYS = 7;
/** The calendar never asks for more than this, so a Month view plus its edges fits. */
export const PORTING_CALENDAR_MAX_DAYS = 42;

export const PORTING_CONFIGURATION_STATUS = {
  CONFIGURED: 'CONFIGURED',
  PARTIALLY_CONFIGURED: 'PARTIALLY_CONFIGURED',
  NOT_CONFIGURED: 'NOT_CONFIGURED',
} as const;

export type PortingConfigurationStatus =
  typeof PORTING_CONFIGURATION_STATUS[keyof typeof PORTING_CONFIGURATION_STATUS];

/**
 * Configured when every number has pre-provision settings, partially when some do,
 * not when none do. An order with no numbers has nothing left to configure.
 */
export function portingConfigurationStatus(configuredTns: number, totalTns: number): PortingConfigurationStatus {
  if (configuredTns >= totalTns) return PORTING_CONFIGURATION_STATUS.CONFIGURED;
  if (configuredTns > 0) return PORTING_CONFIGURATION_STATUS.PARTIALLY_CONFIGURED;
  return PORTING_CONFIGURATION_STATUS.NOT_CONFIGURED;
}

export const PORT_TYPE_FILTER = {
  TOLLFREE: 'TOLLFREE',
  LOCAL: 'LOCAL',
} as const;

export type PortTypeFilter = typeof PORT_TYPE_FILTER[keyof typeof PORT_TYPE_FILTER];

/** Admin filter: orders placed through the management portal (a portal customer), or directly in CPaaS. */
export const PORTING_CHANNEL = {
  PORTAL: 'PORTAL',
  DIRECT: 'DIRECT',
} as const;

export type PortingChannel = typeof PORTING_CHANNEL[keyof typeof PORTING_CHANNEL];

/** A porting day as the API takes it: YYYY-MM-DD, read as a US/Central day. */
export const PORTING_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export interface PortingDateWindow {
  from: Date;
  to: Date;
}

/**
 * `days` calendar days starting today, as a half-open [from, to) window of instants.
 * Days are in `timeZone`: the dashboard passes the viewer's, so "today" and "the next
 * seven days" are theirs. Porting days (US/Central) otherwise.
 */
export function portingDaysFromToday(
  days: number,
  now: Date = new Date(),
  timeZone: string = PORTING_TIME_ZONE,
): PortingDateWindow {
  const start = moment.tz(now, timeZone).startOf('day');
  return { from: start.toDate(), to: start.clone().add(days, 'days').toDate() };
}

/**
 * A caller's inclusive date range as a half-open window of instants: `from` at the
 * start of its day, `to` through the end of its day. Days are porting days unless the
 * caller names its own zone, as the calendar does with the viewer's.
 */
export function portingDateRange(
  from: Date | string,
  to: Date | string,
  timeZone: string = PORTING_TIME_ZONE,
): PortingDateWindow {
  return {
    from: moment.tz(from, timeZone).startOf('day').toDate(),
    to: moment.tz(to, timeZone).startOf('day').add(1, 'day').toDate(),
  };
}

// ---------------------------------------------------------------------------
// Issue / Next Action
// ---------------------------------------------------------------------------

export interface PortingNextActionOrder {
  status: string | null;
  errors?: TnPortOrderError[] | null;
  remarks?: string | null;
  focConfirmed?: boolean;
  configuredTns?: number;
  totalTns?: number;
}

/** What customers see in place of a submission issue's carrier detail, which is Nuso's to fix. */
export const PORTING_SUBMISSION_ISSUE_CUSTOMER_TEXT = 'Nuso is resolving a submission issue';

const STATUS_NEXT_ACTION: Partial<Record<string, string>> = {
  [PORT_STATUS.NEW]: 'Awaiting submission',
  [PORT_STATUS.TOLLFREE_NEW]: 'Awaiting submission',
  [PORT_STATUS.HOLD]: 'On hold until the order it replaces is cancelled',
  [PORT_STATUS.SUBMISSION_PENDING]: 'Submitting to carrier',
  [PORT_STATUS.UPDATE_POSTED]: 'Awaiting carrier response to update',
  [PORT_STATUS.DATE_CHANGE_POSTED]: 'Awaiting carrier response to date change',
  [PORT_STATUS.DATE_CHANGE_PENDING]: 'Awaiting carrier response to date change',
  [PORT_STATUS.UPDATE_RECEIVED]: 'Awaiting carrier response to update',
  [PORT_STATUS.UPDATE_PENDING_FOC]: 'Awaiting FOC from carrier',
  [PORT_STATUS.FOC_PENDING]: 'Awaiting FOC from carrier',
  [PORT_STATUS.FOC_RECEIVED]: 'Port scheduled',
  [PORT_STATUS.LSR_APPROVED]: 'Port scheduled',
  [PORT_STATUS.ACTIVATION_PENDING]: 'Activation pending',
  [PORT_STATUS.ACTIVATION_IN_PROGRESS]: 'Activation in progress',
  [PORT_STATUS.CANCELLATION_PENDING]: 'Cancellation pending',
  [PORT_STATUS.REJECTED]: 'Correct the rejected order information',
  [PORT_STATUS.SUBMISSION_FAILED]: 'Resubmit to carrier',
  [PORT_STATUS.ACTIVATION_CONFLICT]: 'Resolve activation conflict',
  [PORT_STATUS.ACTIVATION_FAILED]: 'Retry activation',
  [PORT_STATUS.FAILED]: 'Investigate failed order',
  [PORT_STATUS.CANCELLATION_FAILED]: 'Retry cancellation',
  [PORT_STATUS.PARTIAL_COMPLETE]: 'Resolve numbers that did not complete',
};

/** The carrier's reason, most specific first: its elaboration, its message, then remarks. */
function carrierReason(order: PortingNextActionOrder): string | null {
  const error = order.errors?.find(e => e?.additionalErrorMessage || e?.errorMessage);
  return error?.additionalErrorMessage || error?.errorMessage || order.remarks?.trim() || null;
}

/**
 * The Issue / Next Action text for one order: the carrier's reason where there is a
 * problem, otherwise a standard sentence for its status. A customer never sees the
 * detail of a submission issue, which is Nuso's to fix. Null when nothing is left to do.
 */
export function portingNextAction(order: PortingNextActionOrder, { viewerIsAdmin }: { viewerIsAdmin: boolean }): string | null {
  const status = order.status ?? PORT_STATUS.NEW;

  if (SUBMISSION_ISSUE_STATUSES.includes(status)) {
    return viewerIsAdmin
      ? carrierReason(order) ?? STATUS_NEXT_ACTION[status] ?? null
      : PORTING_SUBMISSION_ISSUE_CUSTOMER_TEXT;
  }
  if (status === PORT_STATUS.REJECTED) {
    return carrierReason(order) ?? STATUS_NEXT_ACTION[status] ?? null;
  }
  // Scheduled but not ready: the one thing still to do before the numbers move.
  if (order.focConfirmed && (order.configuredTns ?? 0) < (order.totalTns ?? 0)
    && status !== PORT_STATUS.COMPLETED && status !== PORT_STATUS.CANCELLED) {
    return 'Configure numbers before the port date';
  }
  return STATUS_NEXT_ACTION[status] ?? null;
}

/**
 * The group's next action is its most urgent order's: one the customer must fix,
 * then one Nuso must fix (its carrier detail for an admin, the generic line for a
 * customer, since it is why their port is held up), then the order porting soonest,
 * then whichever has anything to say.
 */
export function portingGroupNextAction<T extends PortingNextActionOrder & { portDate?: Date | string | null }>(
  orders: T[],
  options: { viewerIsAdmin: boolean },
): string | null {
  const rank = (order: T) => {
    const status = order.status ?? PORT_STATUS.NEW;
    if (status === PORT_STATUS.REJECTED) return 0;
    if (SUBMISSION_ISSUE_STATUSES.includes(status)) return options.viewerIsAdmin ? 1 : 2;
    return 3;
  };
  const time = (order: T) => (order.portDate ? new Date(order.portDate).getTime() : Number.POSITIVE_INFINITY);
  const ordered = [...orders].sort((a, b) => rank(a) - rank(b) || time(a) - time(b));
  for (const order of ordered) {
    const action = portingNextAction(order, options);
    if (action) return action;
  }
  return null;
}

// ---------------------------------------------------------------------------
// API shapes
// ---------------------------------------------------------------------------

export interface PortingDashboardFilter {
  search?: string | null;
  // Sidewinder accounts, matching their orders that have no portal customer.
  customerIds?: number[] | null;
  portalCustomerIds?: number[] | null;
  // Admin only; ignored for anyone else.
  channel?: PortingChannel | null;
  portTypes?: PortTypeFilter[] | null;
  configuration?: PortingConfigurationStatus[] | null;
  // An inclusive range of days in timeZone, YYYY-MM-DD. Narrows every tile by port
  // date, and is the Upcoming Ports range (default: the next
  // PORTING_DASHBOARD_UPCOMING_DAYS days). Strings, not Dates: a Date would be an
  // instant, and midnight UTC is the previous day in the Americas.
  fromDate?: string | null;
  toDate?: string | null;
  // The viewer's IANA zone, which every day above and every day-based tile window is
  // in. Porting time (US/Central) when omitted or unknown.
  timeZone?: string | null;
}

export type PortingDashboardCounts = Record<PortingDashboardTile, number | null>;

export interface PortingDashboardChildOrder {
  orderId: string;
  portOrderId: string | null;
  displayId: string | null;
  status: string | null;
  portType: string | null;
  losingSpid: string | null;
  endUserName: string | null;
  portDate: Date | null;
  focConfirmed: boolean;
  totalTns: number;
  configuredTns: number;
  configurationStatus: PortingConfigurationStatus;
  salesforceCaseNumber: string | null;
  nextAction: string | null;
}

export interface PortingDashboardOrder {
  groupId: string;
  displayId: string | null;
  accountId: number | null;
  accountName: string | null;
  portalCustomerId: number | null;
  // The portal customer's name where there is one, else the account's.
  customerName: string | null;
  endUserName: string | null;
  pon: string | null;
  tnQuantity: number;
  spids: string[];
  status: GroupSummaryStatus;
  needsAttention: boolean;
  hasSubmissionIssue: boolean;
  nextAction: string | null;
  portDate: Date | null;
  portDateConfirmed: boolean;
  configuredTns: number;
  configurationStatus: PortingConfigurationStatus;
  unreadNoteCount: number;
  lastActivityAt: Date | null;
  salesforceCaseNumbers: string[] | null;
  createdAt: Date;
  orders: PortingDashboardChildOrder[];
}

export interface PortingCalendarCard {
  groupId: string;
  displayId: string | null;
  accountId: number | null;
  accountName: string | null;
  portalCustomerId: number | null;
  customerName: string | null;
  portAt: Date;
  // Only the numbers porting on this day: a group whose orders port on several
  // days appears once on each of them.
  tnQuantity: number;
  configuredTns: number;
  configurationStatus: PortingConfigurationStatus;
  salesforceCaseNumbers: string[] | null;
}

export interface PortingDashboardCustomerOption {
  // PORTAL: a management-portal customer (customers.id). ACCOUNT: a sidewinder account's
  // orders with no portal customer (the account id). Not `id`: see the GraphQL type.
  kind: 'PORTAL' | 'ACCOUNT';
  customerId: number;
  name: string | null;
}

export interface PortingCalendarDay {
  // YYYY-MM-DD, a porting (US/Central) day.
  date: string;
  totalPorts: number;
  configuredPorts: number;
  notConfiguredPorts: number;
  cards: PortingCalendarCard[];
}
